// v2 cockpit — the KPI row (owner, 2026-10-22). Seven cards (the 7th, « Coût
// IA », 2026-10-22 evening — compact, the row still fits 1440×900); « Profit
// net » opens the « Calculateur de profit » (ProfitCalculator.tsx). Cards: label, change vs the
// previous equal period, the number, one note line, and a 14-day sparkline (one
// point a day over the last 14 days, whatever the period — said in the
// tooltip). A card whose change is bad gets the alert border (lib/homeDashboard
// kpiAlert). Missing data is « — » + « données manquantes », never 0.
// Numbers: worker GET /dashboard/overview → kpis + series (lib/homeKpis.ts).
// Coût IA réel / payé (2026-10-23): the « Coût IA » card shows both — Réel (the
// full value, OpenRouter + Claude even when its free credit covers it) and Payé
// (the cash actually spent) — and « Profit net » (avec coût IA réel, the
// default) also says the figure « avec coût IA payé » when it differs.
import { useState, type ReactNode } from 'react';
import type { AdBasis, OverviewResponse, SeriesPoint } from '@/lib/dashboardApi';
import {
  deltaChip, deliveryChip, formatNum, formatDecimal, kpiAlert, previousLabel, sparkSegments,
  type HomePeriod, type KpiKey,
} from '@/lib/homeDashboard';
import { useT } from '@/lib/i18n';
import { CARD_BORDER, DeltaChip, HEADING_FONT } from './homeParts';

type TFn = ReturnType<typeof useT>['t'];

const LINE = {
  gold: '#C9A24A',
  green: 'var(--color-status-done)',
  red: 'var(--color-status-stuck)',
  amber: 'var(--color-status-waiting)',
} as const;

function Spark({ values, color, title, connect }: { values: Array<number | null>; color: keyof typeof LINE; title: string; connect?: boolean }) {
  const segs = sparkSegments(values, 26, 3, connect);
  return (
    <svg viewBox="0 0 100 26" preserveAspectRatio="none" className="block w-full h-[26px] mt-1" role="img" aria-label={title}>
      <title>{title}</title>
      {segs.map((pts, i) => (
        <polyline key={i} points={pts} fill="none" style={{ stroke: LINE[color] }} strokeWidth={1.6}
          vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
      ))}
    </svg>
  );
}

function Kpi({ label, chip, value, unit, note, missing, alert, spark, sparkColor, sparkTitle, connect, onClick, clickTitle, className = '' }: {
  label: string; chip: ReactNode; value: string | null; unit?: string; note: ReactNode; missing?: boolean;
  alert: boolean; spark: Array<number | null>; sparkColor: keyof typeof LINE; sparkTitle: string; connect?: boolean;
  /** The card opens something (the profit calculator). */
  onClick?: () => void; clickTitle?: string; className?: string;
}) {
  const cls = `bg-white rounded-xl border px-3 xl:px-2.5 pt-[11px] pb-2 min-w-0 text-left ${alert ? 'border-status-stuck-border ring-1 ring-inset ring-status-stuck-border' : CARD_BORDER} ${
    onClick ? 'cursor-pointer hover:border-adm-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-adm-gold transition-colors' : ''} ${className}`;
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag data-kpi={label} data-alert={alert ? '1' : undefined} className={cls}
      {...(onClick ? { type: 'button' as const, onClick, title: clickTitle, 'aria-haspopup': 'dialog' as const } : {})}>
      <div className="flex items-center justify-between gap-1 text-[11.5px] xl:text-[11px] text-adm-muted font-medium min-w-0">
        <span className="truncate" title={label}>{label}</span>
        {chip}
      </div>
      <div className={`text-[25px] leading-[1.1] font-medium tracking-[-0.015em] mt-[3px] truncate ${missing ? 'text-adm-faint' : 'text-adm-ink'}`}
        style={{ fontFamily: HEADING_FONT }}>
        {value ?? '—'}
        {value != null && unit && <small className="font-sans text-[12px] text-adm-muted font-medium ml-[3px]">{unit}</small>}
      </div>
      <div className="text-[11px] leading-[1.3] text-adm-muted mt-0.5 line-clamp-2 min-h-[2.6em]">{note}</div>
      <Spark values={spark} color={sparkColor} title={sparkTitle} connect={connect} />
    </Tag>
  );
}

const Missing = ({ t, why }: { t: TFn; why?: string | null }) => (
  <span className="text-status-waiting font-semibold" title={why ?? undefined}>{t('données manquantes')}</span>
);

// « Pub par … » switch (owner 2026-10-22: « is this per confirmed order or per
// lead? add an option where I can switch between them »). Remembered per device.
const AD_BASIS_KEY = 'sz-home-ad-basis';
const AD_BASIS_OPTIONS: Array<{ key: AdBasis; short: string; label: string; unit: string }> = [
  { key: 'lead', short: 'Lead', label: 'Pub / lead', unit: 'leads' },
  { key: 'order', short: 'Cmd', label: 'Pub / commande', unit: 'commandes' },
  { key: 'confirmed', short: 'Conf.', label: 'Pub / confirmée', unit: 'confirmées' },
  { key: 'delivered', short: 'Livr.', label: 'Pub / livrée', unit: 'livrées' },
];
function readAdBasis(): AdBasis {
  try {
    const v = localStorage.getItem(AD_BASIS_KEY);
    if (v && AD_BASIS_OPTIONS.some((o) => o.key === v)) return v as AdBasis;
  } catch { /* private mode */ }
  return 'confirmed';
}

function AdBasisSwitch({ value, onChange, t }: { value: AdBasis; onChange: (b: AdBasis) => void; t: TFn }) {
  return (
    <span role="group" aria-label={t('Pub par')} className="inline-flex rounded-md border border-[#ECE5D6] dark:border-adm-line p-px gap-px align-middle">
      {AD_BASIS_OPTIONS.map((o) => (
        <button key={o.key} type="button" aria-pressed={value === o.key} title={t(o.label)}
          onClick={(e) => { e.stopPropagation(); onChange(o.key); }}
          className={`px-1 py-px rounded text-[10px] font-semibold leading-[1.4] ${value === o.key
            ? 'bg-[#2B2116] text-[#FBF7EC] dark:bg-adm-gold dark:text-[#2B2116]' : 'text-adm-muted hover:text-adm-ink'}`}>
          {t(o.short)}
        </button>
      ))}
    </span>
  );
}

export function KpiCards({ data, period, onProfitClick }: { data: OverviewResponse; period: HomePeriod; onProfitClick?: () => void }) {
  const { t, lang } = useT();
  const k = data.kpis!.current;
  const q = data.kpis!.previous;
  const s: SeriesPoint[] = data.series ?? [];
  const adsWhy = data.money.current.ads.reason;
  const sparkTitle = t('14 derniers jours, un point par jour (quelle que soit la période)');
  const col = (f: (p: SeriesPoint) => number | null) => s.map(f);
  const alert = (key: KpiKey) => kpiAlert(key, k, q);
  const margin = k.margin != null ? Math.round(k.margin * 100) : null;
  const [basis, setBasis] = useState<AdBasis>(readAdBasis);
  const pickBasis = (b: AdBasis) => { setBasis(b); try { localStorage.setItem(AD_BASIS_KEY, b); } catch { /* private mode */ } };
  const adOpt = AD_BASIS_OPTIONS.find((o) => o.key === basis)!;
  // An older worker sends no adBasis: fall back to the per-order figure.
  const adNow = k.adBasis ? k.adBasis[basis].perUnit : basis === 'order' ? k.adPerOrder : null;
  const adPrev = q?.adBasis ? q.adBasis[basis].perUnit : basis === 'order' ? q?.adPerOrder ?? null : null;
  const adCount = k.adBasis ? k.adBasis[basis].count : k.newOrders;
  const dChip = deliveryChip(k.delivery);

  return (
    <>
      <Kpi label={t('Encaissé')} chip={<DeltaChip c={deltaChip(k.revenue, q?.revenue)} />}
        value={formatNum(k.revenue)} unit="DH" alert={alert('revenue')}
        note={k.delivered === 1 ? t('1 colis livré') : t('{n} colis livrés', { n: k.delivered })}
        spark={col((p) => p.revenue)} sparkColor={alert('revenue') ? 'red' : 'gold'} sparkTitle={sparkTitle} />

      <Kpi label={t('Profit net')} chip={<DeltaChip c={deltaChip(k.profitNet, q?.profitNet)} />}
        value={k.profitNet != null ? formatNum(k.profitNet) : null} unit="DH" missing={k.profitNet == null} alert={alert('profit')}
        onClick={onProfitClick} clickTitle={t('Ouvrir le calculateur de profit')}
        note={k.profitNet == null
          ? (k.missingParts?.includes('ads') ?? true)
            ? <><Missing t={t} why={adsWhy} /> · {t('{x} DH avant pub', { x: formatNum(k.profitBeforeAds) })}</>
            : <><Missing t={t} why={data.money.current.ai?.reason} /> · {t('{x} DH avant coût IA', { x: formatNum(k.profitKnown ?? k.profitBeforeAds) })}</>
          : <>
            <span title={t('avec coût IA réel')}>{margin != null ? t("{n} % de l'encaissé", { n: margin }) : '—'}</span>
            {k.profitNetPaid != null && k.profitNetPaid !== k.profitNet && (
              <span data-profit-paid className="block truncate" title={t('Profit net avec coût IA payé (OpenRouter + Claude hors crédit)')}>
                {t('avec IA payé : {x} DH', { x: formatNum(k.profitNetPaid) })}
              </span>
            )}
          </>}
        spark={col((p) => p.profitNet)} sparkColor={alert('profit') ? 'red' : 'green'} sparkTitle={sparkTitle} />

      <Kpi label={t('Commandes')} chip={<DeltaChip c={deltaChip(k.newOrders, q?.newOrders)} />}
        value={String(k.newOrders)} alert={alert('orders')}
        note={k.confirmation ? (() => {
          // Taux de confirmation (owner 2026-10-22): both · website · WhatsApp — an agent's
          // « ✓ Confirmer » on the leads created in the period.
          const c = k.confirmation;
          const pct = (r: number | null) => (r == null ? '—' : `${Math.round(r * 100)}%`);
          return (
            <span className="grid grid-cols-3 gap-x-1 leading-[1.25]" title={t('Confirmées : {a}/{b} leads · Site {c}/{d} · WhatsApp {e}/{f}', {
              a: c.all.confirmed, b: c.all.leads, c: c.site.confirmed, d: c.site.leads, e: c.whatsapp.confirmed, f: c.whatsapp.leads })}>
              {([['Conf.', c.all.rate], ['Site', c.site.rate], ['WA', c.whatsapp.rate]] as const).map(([lab, r]) => (
                <span key={lab} className="min-w-0">
                  <span className="block text-[10px] text-adm-faint truncate">{t(lab)}</span>
                  <b className="block text-[12px] text-adm-ink">{pct(r)}</b>
                </span>
              ))}
            </span>
          );
        })() : q ? t('nouvelles · {n} {prev}', { n: q.newOrders, prev: t(previousLabel(period)) }) : t('nouvelles')}
        spark={col((p) => p.newOrders)} sparkColor={alert('orders') ? 'red' : 'gold'} sparkTitle={sparkTitle} />


      <Kpi label={t('Panier moyen')} chip={<DeltaChip c={deltaChip(k.avgBasket, q?.avgBasket)} />}
        value={k.avgBasket != null ? formatNum(k.avgBasket) : null} unit="DH" missing={k.avgBasket == null} alert={alert('basket')}
        note={k.avgBasket == null ? t('aucun colis livré')
          : k.avgProfit != null ? t('profit moyen {x} DH / commande', { x: formatNum(k.avgProfit) })
            : <><Missing t={t} why={adsWhy} /> · {t('profit moyen')}</>}
        spark={col((p) => p.avgBasket)} connect sparkColor={alert('basket') ? 'red' : 'gold'} sparkTitle={sparkTitle} />

      <Kpi label={t(adOpt.label)} chip={<DeltaChip c={deltaChip(adNow, adPrev, { invert: true })} />}
        value={adNow != null ? formatNum(adNow) : null} unit="DH" missing={adNow == null} alert={basis === 'order' ? alert('adPerOrder') : false}
        note={<>
          <AdBasisSwitch value={basis} onChange={pickBasis} t={t} />
          <span className="block truncate mt-0.5">
            {k.adSpend == null ? <Missing t={t} why={adsWhy} />
              : t('{x} DH de pub · {n} {unit}', { x: formatNum(k.adSpend), n: adCount, unit: t(adOpt.unit) })
                + (k.roas != null ? ` · ×${formatDecimal(k.roas, lang)}` : '')}
          </span>
        </>}
        spark={basis === 'order' ? col((p) => p.adPerOrder) : col((p) => p.adSpend)}
        sparkColor={basis === 'order' && alert('adPerOrder') ? 'red' : 'amber'}
        sparkTitle={basis === 'order' ? sparkTitle : t('Dépense pub par jour — 14 derniers jours')} />

      <Kpi label={t('Livraison')}
        chip={dChip ? <DeltaChip c={{ text: dChip.tooEarly ? t('{n} colis — trop tôt', { n: dChip.n }) : dChip.text, tone: dChip.tone }}
          title={t('Taux de livraison réussie (livrés ÷ livrés + retours)')} /> : null}
        value={k.delivery.avgDays != null ? formatDecimal(k.delivery.avgDays, lang) : null} unit={t('jours')}
        missing={k.delivery.avgDays == null} alert={alert('delivery')}
        note={`${k.delivery.delivered === 1 ? t('1 colis livré') : t('{n} colis livrés', { n: k.delivery.delivered })} · ${
          k.delivery.returned <= 1 ? t('{n} retour', { n: k.delivery.returned }) : t('{n} retours', { n: k.delivery.returned })}`}
        spark={col((p) => p.deliveryDays)} connect sparkColor={alert('delivery') ? 'red' : 'green'} sparkTitle={sparkTitle} />

      {/* Coût IA (2026-10-22): Σ ai_calls in the period, MAD. Older worker: no card. */}
      {k.aiCost !== undefined && (
        <Kpi label={t('Coût IA')} chip={null}
          value={k.aiCost != null ? formatNum(k.aiCost) : null} unit="DH" missing={k.aiCost == null} alert={false}
          className="col-span-2 md:col-span-1"
          note={k.aiCost == null ? <Missing t={t} why={data.money.current.ai?.reason} />
            : <>
              {k.aiPaid !== undefined && (
                <span data-ai-paid className="block truncate" title={t('Réel = valeur de tous les appels · Payé = OpenRouter + Claude hors crédit')}>
                  {t('Réel')} · {t('payé {x} DH', { x: formatNum(k.aiPaid ?? k.aiCost) })}
                </span>
              )}
              {k.aiPerOrder != null ? t('{x} DH / commande', { x: formatDecimal(k.aiPerOrder, lang) }) : t('aucune commande')}{k.aiPartial ? ` · ${t('partiel')}` : ''}
            </>}
          spark={col((p) => p.aiCost ?? null)} sparkColor="amber" sparkTitle={sparkTitle} />
      )}
    </>
  );
}

export function HomeKpiRow({ data, isLoading, period, onProfitClick }: { data?: OverviewResponse; isLoading: boolean; period: HomePeriod; onProfitClick?: () => void }) {
  const seven = data?.kpis?.current.aiCost !== undefined;
  // The 7th card (Coût IA) is the compact one: no chip, a narrower column.
  const grid = `grid grid-cols-2 md:grid-cols-3 ${seven ? 'xl:grid-cols-[repeat(6,minmax(0,1fr))_minmax(0,0.6fr)]' : 'xl:grid-cols-6'} gap-2 lg:gap-2.5`;
  if (!isLoading && !data?.kpis) return null; // an older worker: no KPI row rather than a fake one
  if (isLoading || !data?.kpis) {
    return (
      <section aria-busy={isLoading} className={grid}>
        {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-[104px] rounded-xl bg-adm-surface-3 animate-pulse" />)}
      </section>
    );
  }
  return <section aria-label="KPI" className={grid}><KpiCards data={data} period={period} onProfitClick={onProfitClick} /></section>;
}
